import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { followUpApi, leadApi } from "@/services/api";
import { formatError } from "@/services/api";
import { Clock, CheckCircle2, RefreshCw, AlertTriangle, ChevronRight, Loader2, Plus, X } from "lucide-react";
import { FOLLOW_UPS } from "@/constants/testIds";
import { toast } from "sonner";

const TYPE_COLOR = {
  WAITING_FOR_CUSTOMER: "text-[#8C4B00] bg-amber-400/10",
  PROPOSAL_SENT:        "text-[#0176D3] bg-blue-400/10",
  MEETING_REQUESTED:    "text-indigo-400 bg-indigo-400/10",
  CUSTOMER_INACTIVE:    "text-rose-400 bg-rose-400/10",
  POC_ACTION_REQUIRED:  "text-[#8C4B00] bg-orange-400/10",
  GENERAL:              "text-[#706E6B] bg-slate-400/10",
};

const FOLLOW_UP_TYPES = [
  { value: "GENERAL",              label: "General" },
  { value: "WAITING_FOR_CUSTOMER", label: "Waiting for Customer" },
  { value: "PROPOSAL_SENT",        label: "Proposal Sent" },
  { value: "MEETING_REQUESTED",    label: "Meeting Requested" },
  { value: "CUSTOMER_INACTIVE",    label: "Customer Inactive" },
  { value: "POC_ACTION_REQUIRED",  label: "Action Required" },
];

const TABS = [
  { key: "overdue",    label: "Overdue",    icon: AlertTriangle, color: "text-rose-400" },
  { key: "scheduled",  label: "Scheduled",  icon: Clock,         color: "text-[#8C4B00]" },
  { key: "completed",  label: "Completed",  icon: CheckCircle2,  color: "text-[#2E844A]" },
];

export default function FollowUps() {
  const navigate = useNavigate();
  const [tab,          setTab]          = useState("overdue");
  const [items,        setItems]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [completing,   setCompleting]   = useState({});
  const [rescheduleId, setRescheduleId] = useState(null);
  const [showCreate,   setShowCreate]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params =
        tab === "overdue"    ? { overdue_only: true }   :
        tab === "scheduled"  ? { status: "SCHEDULED" }  :
        { status: "COMPLETED" };
      const { data } = await followUpApi.list(params);
      setItems(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const handleComplete = async (id) => {
    setCompleting(p => ({ ...p, [id]: true }));
    try {
      await followUpApi.complete(id, { notes: "Completed via dashboard" });
      toast.success("Follow-up marked as completed");
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setCompleting(p => ({ ...p, [id]: false }));
    }
  };

  return (
    <div className="p-3 sm:p-6 max-w-4xl mx-auto space-y-5">
      {showCreate && (
        <CreateFollowUpModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-[#0176D3]" />
          <h1 className="text-xl font-bold text-[#181818] font-sans">Follow-ups</h1>
          <span className="text-xs font-mono text-[#706E6B] bg-[#F3F3F3] border border-[#E5E5E5] px-2 py-0.5 rounded">
            {items.length} {tab}
          </span>
        </div>
        <button
          data-testid="new-followup-btn"
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#EEF4FF] border border-[#0176D3]/30 rounded-lg text-xs text-[#0176D3] hover:bg-[#EEF4FF] transition-colors font-medium"
        >
          <Plus className="w-3.5 h-3.5" /> New Follow-up
        </button>
      </div>

      {/* Tabs — scrollable on mobile */}
      <div className="overflow-x-auto pb-1">
        <div className="flex gap-1 p-1 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl w-fit">
          {TABS.map(({ key, label, icon: Icon, color }) => (
            <button
              key={key}
              data-testid={key === "overdue" ? FOLLOW_UPS.overdueTab : undefined}
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm transition-all ${
                tab === key
                  ? "bg-[#EEF4FF] text-[#0176D3] font-semibold"
                  : "text-[#706E6B] hover:text-[#444444]"
              }`}>
              <Icon className={`w-3.5 h-3.5 ${tab === key ? "" : color}`} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl overflow-hidden" data-testid={FOLLOW_UPS.list}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-[#0176D3]" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center">
            <Clock className="w-8 h-8 text-[#706E6B] mx-auto mb-3" />
            <p className="text-sm text-[#706E6B]">No {tab} follow-ups</p>
            {tab !== "completed" && (
              <button
                onClick={() => setShowCreate(true)}
                className="mt-3 flex items-center gap-1.5 mx-auto px-4 py-2 bg-[#EEF4FF] border border-[#0176D3]/30 rounded-lg text-xs text-[#0176D3] hover:bg-[#EEF4FF] transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Schedule Follow-up
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-[#E5E5E5]/50">
            {items.map(fu => (
              <div key={fu.id} className="flex items-start gap-4 px-5 py-4" data-testid={FOLLOW_UPS.item}>
                <div className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${tab === "overdue" ? "bg-rose-400" : tab === "completed" ? "bg-emerald-400" : "bg-amber-400"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${TYPE_COLOR[fu.follow_up_type] || TYPE_COLOR.GENERAL}`}>
                          {FOLLOW_UP_TYPES.find(t => t.value === fu.follow_up_type)?.label || fu.follow_up_type?.replace(/_/g, " ")}
                        </span>
                        {tab === "overdue" && (
                          <span className="text-[10px] font-mono text-rose-400 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> OVERDUE
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#181818] mt-1 font-sans">{fu.reason}</p>
                      <div className="flex items-center gap-3 mt-1.5">
                        <button
                          onClick={() => navigate(`/engage/leads/${fu.lead_id}`)}
                          className="flex items-center gap-1 text-xs text-[#0176D3] hover:text-sky-300 transition-colors">
                          View Lead <ChevronRight className="w-3 h-3" />
                        </button>
                        <span className="text-xs text-[#706E6B]">
                          {tab === "completed"
                            ? `Completed ${formatDate(fu.completed_at)}`
                            : `Scheduled ${formatDateTime(fu.scheduled_at)}`}
                        </span>
                      </div>
                      {fu.completion_notes && (
                        <p className="text-xs text-[#706E6B] mt-1 italic">"{fu.completion_notes}"</p>
                      )}
                    </div>
                    {tab !== "completed" && (
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          data-testid={FOLLOW_UPS.rescheduleBtn}
                          onClick={() => setRescheduleId(fu.id)}
                          className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-[#E5E5E5] rounded text-[#706E6B] hover:text-[#181818] hover:border-slate-500 transition-colors">
                          <RefreshCw className="w-3 h-3" /> Reschedule
                        </button>
                        <button
                          data-testid={FOLLOW_UPS.completeBtn}
                          onClick={() => handleComplete(fu.id)}
                          disabled={completing[fu.id]}
                          className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded text-[#2E844A] hover:bg-emerald-500/20 disabled:opacity-60 transition-all">
                          {completing[fu.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                          Complete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reschedule Modal */}
      {rescheduleId && (
        <RescheduleModal
          fuId={rescheduleId}
          onClose={() => setRescheduleId(null)}
          onRescheduled={load}
        />
      )}
    </div>
  );
}

// ── Create Follow-up Modal ─────────────────────────────────────────────────────

const fInp = "w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#181818] focus:outline-none focus:border-[#0176D3]/50 focus:ring-1 focus:ring-[#0176D3]/20";

function CreateFollowUpModal({ onClose, onCreated }) {
  const [leads,  setLeads]  = useState([]);
  const [form,   setForm]   = useState({ lead_id: "", follow_up_type: "GENERAL", scheduled_at: "", reason: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    leadApi.list({ limit: 50 }).then(r => setLeads(r.data.results || [])).catch(() => {});
  }, []);

  const set = (k, v) => { setForm(p => ({...p, [k]: v})); setErrors(p => ({...p, [k]: ""})); };

  const validate = () => {
    const e = {};
    if (!form.lead_id)         e.lead_id      = "Related lead is required";
    if (!form.scheduled_at)    e.scheduled_at = "Date and time is required";
    if (!form.reason.trim())   e.reason       = "Follow-up subject / reason is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await followUpApi.create({
        lead_id:         form.lead_id,
        follow_up_type:  form.follow_up_type,
        scheduled_at:    new Date(form.scheduled_at).toISOString(),
        reason:          form.reason.trim(),
      });
      toast.success("Follow-up scheduled");
      onCreated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errs = {};
        detail.forEach(d => { errs[d.field] = d.message; });
        setErrors(errs);
      } else {
        toast.error(typeof detail === "string" ? detail : formatError(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="bg-white rounded-xl border border-[#E5E5E5] w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#0176D3]" />
            <h2 className="text-base font-semibold text-[#181818]">New Follow-up</h2>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-[#706E6B]" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {/* Related Lead */}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Related Lead <span className="text-rose-500">*</span></label>
            <select
              value={form.lead_id}
              onChange={e => set("lead_id", e.target.value)}
              data-testid="followup-lead-select"
              className={`${fInp}${errors.lead_id ? " border-rose-400" : ""}`}
            >
              <option value="">— Select Lead —</option>
              {leads.map(l => (
                <option key={l.id} value={l.id}>{l.lead_number} · {l.customer_name}</option>
              ))}
            </select>
            {errors.lead_id && <p data-testid="err-followup-lead" className="text-xs text-rose-500 mt-1">{errors.lead_id}</p>}
          </div>

          {/* Follow-up Type */}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Follow-up Type</label>
            <select
              value={form.follow_up_type}
              onChange={e => set("follow_up_type", e.target.value)}
              data-testid="followup-type-select"
              className={fInp}
            >
              {FOLLOW_UP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          {/* Scheduled At */}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Date & Time <span className="text-rose-500">*</span></label>
            <input
              type="datetime-local"
              value={form.scheduled_at}
              onChange={e => set("scheduled_at", e.target.value)}
              data-testid="followup-scheduled-at"
              className={`${fInp}${errors.scheduled_at ? " border-rose-400" : ""}`}
            />
            {errors.scheduled_at && <p data-testid="err-followup-scheduled" className="text-xs text-rose-500 mt-1">{errors.scheduled_at}</p>}
          </div>

          {/* Reason / Subject */}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Subject / Reason <span className="text-rose-500">*</span></label>
            <input
              value={form.reason}
              onChange={e => set("reason", e.target.value)}
              placeholder="What is this follow-up for?"
              data-testid="followup-reason"
              className={`${fInp}${errors.reason ? " border-rose-400" : ""}`}
            />
            {errors.reason && <p data-testid="err-followup-reason" className="text-xs text-rose-500 mt-1">{errors.reason}</p>}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#706E6B] hover:text-[#181818]">Cancel</button>
          <button
            data-testid="followup-submit-btn"
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 text-sm bg-[#0176D3] text-white rounded-lg hover:bg-[#0060B0] disabled:opacity-50 flex items-center gap-1.5"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saving ? "Saving…" : "Schedule Follow-up"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Reschedule Modal ────────────────────────────────────────────────────────────

function RescheduleModal({ fuId, onClose, onRescheduled }) {
  const [newTime, setNewTime] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!newTime) { toast.error("Select a new time"); return; }
    setLoading(true);
    try {
      await followUpApi.reschedule(fuId, { rescheduled_at: new Date(newTime).toISOString() });
      toast.success("Follow-up rescheduled");
      onRescheduled();
      onClose();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#F3F3F3]/80">
      <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl w-full max-w-sm mx-4 p-6 space-y-4">
        <h3 className="text-base font-semibold text-[#181818] font-sans">Reschedule Follow-up</h3>
        <div>
          <label className="text-xs text-[#706E6B] mb-1 block">New Date & Time</label>
          <input type="datetime-local" value={newTime} onChange={e => setNewTime(e.target.value)}
            className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#181818] focus:outline-none focus:border-[#0176D3]/30" />
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-[#E5E5E5] rounded-lg text-sm text-[#706E6B] hover:text-[#181818] transition-colors">Cancel</button>
          <button onClick={submit} disabled={loading || !newTime}
            className="flex-1 px-4 py-2 bg-[#EEF4FF] border border-[#0176D3]/30 rounded-lg text-sm text-[#0176D3] hover:bg-[#EEF4FF] disabled:opacity-60 transition-all flex items-center justify-center gap-2">
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Reschedule
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}
function formatDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}
